import * as React from "react";
import {useState} from "react";
import {AdminAnalyticsApi} from "../../../../api/AdminAnalyticsApi";
import {LoadingSpinner} from "../../../icons/LoadingSpinner.component";
import {DEFAULT_PLANS, INTERNAL_ONLY_PLANS, PlanViewTab} from "../shared/analytics.constants";
import {useAnalyticsTabData} from "../hooks/useAnalyticsTabData";
import {PlanEngagementTable} from "./PlanEngagementTable.component";
import {ClientAdoptionTable} from "./ClientAdoptionTable.component";
import {PlanFeatureTable} from "./PlanFeatureTable.component";
import {PlanFunnelChart} from "./PlanFunnelChart.component";
import {PortfolioPlansTable} from "./PortfolioPlansTable.component";

interface IPlansTabProps {
    range: { startDate: string; endDate: string };
    country: string;
}

const isInternalOnlyPlan = (name: string): boolean => INTERNAL_ONLY_PLANS.has(name.toLowerCase());

export const PlansTab: React.FC<IPlansTabProps> = (props) => {
    const {data, loading, error} = useAnalyticsTabData(
        "plans",
        (startDate, endDate, c) => AdminAnalyticsApi.getPlansTab(startDate, endDate, c),
        props.range,
        props.country,
        DEFAULT_PLANS,
    );
    const [planViewTab, setPlanViewTab] = useState<PlanViewTab>("client");

    if (loading) {
        return <div className="admin-analytics__tab-loading"><LoadingSpinner/></div>;
    }
    if (error) {
        return <div className="admin-analytics__error">{error}</div>;
    }

    const planFilter = (planName: string) =>
        planViewTab === "internal" ? isInternalOnlyPlan(planName) : !isInternalOnlyPlan(planName);

    const viewPlanEngagement = data.planEngagement.filter((r) => planFilter(r.planName));
    const viewClientAdoption = data.clientAdoption.filter((r) => planFilter(r.planName));
    const viewPlanFeatureUsage = data.planFeatureUsage.filter((r) => planFilter(r.planName));
    const viewPlanFunnel = data.planFunnel.filter((r) => planFilter(r.planName));
    const viewPortfolioPlans = data.portfolioPlans.filter((r) => planFilter(r.planName));

    const clientCount = data.planEngagement.filter((r) => !isInternalOnlyPlan(r.planName)).length;
    const internalCount = data.planEngagement.filter((r) => isInternalOnlyPlan(r.planName)).length;

    return (
        <div>
            <div className="admin-analytics__subtabs admin-analytics__subtabs--plan">
                <button
                    className={`admin-analytics__subtab${
                        planViewTab === "client" ? " admin-analytics__subtab--active" : ""}`}
                    onClick={() => setPlanViewTab("client")}
                >
                    Client Plans ({clientCount})
                </button>
                <button
                    className={`admin-analytics__subtab${
                        planViewTab === "internal" ? " admin-analytics__subtab--active" : ""}`}
                    onClick={() => setPlanViewTab("internal")}
                >
                    Internal Plans ({internalCount})
                </button>
            </div>
            <PlanEngagementTable
                key={`engagement-${planViewTab}`}
                rows={viewPlanEngagement}
                planViewTab={planViewTab}
                range={props.range}
                country={props.country}
            />
            {viewClientAdoption.length > 0 && (
                <ClientAdoptionTable
                    key={`adoption-${planViewTab}`}
                    rows={viewClientAdoption}
                    range={props.range}
                    country={props.country}
                />
            )}
            {viewPlanFeatureUsage.length > 0 && (
                <PlanFeatureTable
                    key={`feature-${planViewTab}`}
                    rows={viewPlanFeatureUsage}
                    range={props.range}
                    country={props.country}
                />
            )}
            {viewPlanFunnel.length > 0 && (
                <PlanFunnelChart key={`funnel-${planViewTab}`} rows={viewPlanFunnel}/>
            )}
            {viewPortfolioPlans.length > 0 && (
                <PortfolioPlansTable key={`portfolio-${planViewTab}`} rows={viewPortfolioPlans}/>
            )}
        </div>
    );
};
